<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            static
            class="fixed z-10 inset-0 overflow-y-auto"
            @close="open = false"
            :open="open"
        >
            <div
                class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
            >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0"
                >
                    <DialogOverlay
                        class="fixed inset-0 no-bg-opacity-75 transition-all duration-500 ease-in-out bg-opacity-10"
                        :class="{
                            'bg-gray-500': !warning,
                            'bg-red-300': warning,
                        }"
                    />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span
                    class="hidden sm:inline-block sm:align-middle sm:h-screen"
                    aria-hidden="true"
                    >&#8203;</span
                >
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
                    >
                        <div
                            class="mx-auto flex items-center justify-center h-10 w-10 rounded-full bg-indigo-100 fixed -mt-3 ml-auto inset-x-0 top-0 hidden"
                        >
                            <PencilAltIcon class="h-5 w-5 text-indigo-600" />
                        </div>
                        <div class="mt-2">
                            <div class="text-center sm:mt-0">
                                <DialogTitle
                                    as="h3"
                                    class="text-base leading-2 font-medium text-gray-700"
                                >
                                    {{ t("meetings.documents", "Documenten") }}
                                </DialogTitle>
                                <DialogTitle
                                    as="h3"
                                    class="text-lg leading-6 font-medium text-gray-900"
                                >
                                    {{ meeting.title }}
                                </DialogTitle>

                                <DialogTitle
                                    as="h5"
                                    class="text-base leading-2 pt-1 font-medium text-gray-700"
                                >
                                    {{ meeting.datum }}
                                </DialogTitle>

                                <input
                                    type="text"
                                    tabIndex="{0}"
                                    :class="{ hidden: opened }"
                                />

                                <button
                                    class="inline-flex items-center px-4 py-2 mt-1 border border-green-700 rounded-md shadow text-base font-semibold text-green-50 bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                    @click="triggerFileUpload('bestand')"
                                >
                                    <svg
                                        class="fill-current w-4 h-4 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                                        ></path>
                                    </svg>
                                    {{
                                        t("documents.upload", "Upload document")
                                    }}
                                </button>

                                <input
                                    type="file"
                                    id="bestand"
                                    ref="bestand"
                                    v-on:change="addDocument('bestand')"
                                    style="display: none"
                                />

                                <div class="text-left">
                                    <div
                                        class="sm:col-span-2 py-5 pb-2"
                                        v-if="documents.length > 0"
                                    >
                                        <dd
                                            class="mt-1 text-sm leading-5 text-gray-900"
                                        >
                                            <ul
                                                class="border border-gray-200 rounded-md"
                                            >
                                                <li
                                                    class="pl-3 pr-4 py-3 flex items-center justify-between text-sm leading-5 bg-gray-50 hover:bg-white border-b border-color-gray-100"
                                                    v-for="(
                                                        document, subindex
                                                    ) in documents"
                                                    v-bind:key="subindex"
                                                >
                                                    <div
                                                        class="w-0 flex-1 flex items-center"
                                                    >
                                                        <svg
                                                            class="flex-shrink-0 h-5 w-5 text-gray-400"
                                                            fill="currentColor"
                                                            viewBox="0 0 20 20"
                                                        >
                                                            <path
                                                                fill-rule="evenodd"
                                                                d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                                                                clip-rule="evenodd"
                                                            />
                                                        </svg>
                                                        <span
                                                            class="ml-2 flex-1 w-0 break-all"
                                                        >
                                                            {{ document.name }}
                                                            <span
                                                                class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800"
                                                                >{{
                                                                    document.extension
                                                                }}</span
                                                            >
                                                        </span>
                                                    </div>
                                                    <div
                                                        class="ml-4 flex-shrink-0"
                                                    >
                                                        <a
                                                            @click="
                                                                deleteDocument(
                                                                    document
                                                                )
                                                            "
                                                            class="cursor-pointer px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 mr-2"
                                                            >{{
                                                                t(
                                                                    "documents.delete",
                                                                    "wis"
                                                                )
                                                            }}</a
                                                        >
                                                        <a
                                                            :href="document.url"
                                                            class="font-medium text-indigo-600 hover:text-indigo-500 transition duration-150 ease-in-out"
                                                        >
                                                            {{
                                                                t(
                                                                    "download",
                                                                    "Download"
                                                                )
                                                            }}</a
                                                        >
                                                    </div>
                                                </li>
                                            </ul>
                                        </dd>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import Swal from "sweetalert2";

import { ref } from "vue";
import {
    Dialog,
    DialogOverlay,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
} from "@headlessui/vue";
import { CheckIcon, PencilAltIcon } from "@heroicons/vue/outline";
import InfoBox from "../../../components/InfoBox.vue";

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CheckIcon,
        InfoBox,
        PencilAltIcon,
    },
    mounted() {
        this.getDocuments();
        Eventing.listen("closeModal", () => (this.open = false));
    },
    created() {
        this.message = this.t("documents.no-files", "Geen stemmingen bekend");
    },
    data() {
        const open = ref(false);

        return {
            open,
            opened: false,
            documents: null,
            meeting: null,
            warning: false,
            message: "Geen stemmingen bekend",
        };
    },
    methods: {
        getDocuments() {
            var self = this;
            axios
                .get("/api/documenten?meeting_slug=" + self.$route.params.id)
                .then((response) => {
                    console.info("getDocuments");
                    console.info(response.data);
                    this.meeting = response.data.meeting;
                    this.documents = response.data.data;
                    this.open = true;
                    Eventing.fire("formMeeting");
                    setTimeout(() => {
                        self.opened = true;
                    }, 40);
                })
                .catch((error) => {
                    // alert(error.response.status);
                });
        },
        triggerFileUpload(field) {
            document.getElementById(field).click();
        },

        addDocument(field) {
            let self = this;
            this.file = this.$refs.bestand.files[0];
            let formData = new FormData();
            formData.append("bestand", this.file);
            var meeting = this.meeting;
            formData.append("meeting_id", meeting.id);
            axios
                .post("/api/documenten", formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                })
                .then(function (response) {
                    Eventing.fire("notify", {
                        title: self.t("documents.success", "Gelukt"),
                        message: self.t(
                            "documents.added",
                            "Document is toegevoegd"
                        ),
                        type: "success",
                    });
                    self.getDocuments();
                })
                .catch(function (error) {
                    if (error.response) {
                        Eventing.fire("notify", {
                            title: self.t("documents.failure", "Mislukt"),
                            message: self.t(
                                "documents.incorrect-form",
                                "U hebt het formulier invalide of onvolledig ingevuld"
                            ),
                            type: "error",
                        });
                    }
                });
        },
        deleteDocument(document) {
            let self = this;
            Swal.fire({
                title: self.t("documents.delete-title", "Wis bestand"),
                text: self.t(
                    "documents.delete-document",
                    "Zeker weten dat u het bestand :value wilt verwijderen?",
                    document.name
                ),

                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#5850ec",
                cancelButtonColor: "#ddd",
                confirmButtonText: self.t("yes", "Annuleer"),
                cancelButtonText: self.t("cancel", "Annuleer"),
            }).then((result) => {
                if (result.value) {
                    if (true) {
                        axios
                            .delete("/api/documenten/" + document.hash)
                            .then((response) => {
                                Eventing.fire("notify", {
                                    title: self.t("succeeded", "Gelukt"),
                                    message: self.t(
                                        "documents.deleted-success",
                                        "Bestand verwijderd"
                                    ),
                                    type: "warning",
                                });
                                self.getDocuments();
                            })
                            .catch((error) => {
                                if (error.response) {
                                    // alert("Error: " + error.response.status);
                                }
                            });
                    }
                }
            });
        },
    },
};
</script>
